import { Clock4, Loader2, Play, Square } from 'lucide-react'

import { cn } from '@/lib/cn'

export type BigButtonMode = 'start' | 'end' | 'unknown'

/**
 * `gps` i `saving` to praca w toku po kliknięciu, `unavailable` — kliknąć nie
 * wolno (brak potwierdzonego stanu z serwera albo zaległy zapis w kolejce).
 */
export type BigButtonStatus = 'ready' | 'gps' | 'saving' | 'unavailable'

interface BigButtonProps {
  /** Tryb wynikający ze stanu na serwerze; `unknown`, dopóki go nie znamy. */
  mode: BigButtonMode
  status: BigButtonStatus
  /**
   * Id widocznego powodu blokady. Przycisk `disabled` nie dostaje fokusu,
   * więc powód nie może żyć w tooltipie — musi stać pod przyciskiem.
   */
  describedBy?: string
  onClick: () => void
}

export default function BigButton({ mode, status, describedBy, onClick }: BigButtonProps) {
  const isBusy = status === 'gps' || status === 'saving'
  const isUnavailable = status === 'unavailable'
  // Zablokowany wygląda jak obrys na powierzchni, a nie jak przygaszony
  // przycisk: opacity na ciemnym wypełnieniu zbijała etykietę poniżej AA.
  // text-muted na bg-surface: ok. 5,3:1 w jasnym i 5,1:1 w ciemnym motywie.
  const iconClass = cn('h-6 w-6', isUnavailable ? 'text-text-muted' : 'text-brand-accent')

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isBusy || isUnavailable}
      aria-busy={isBusy}
      aria-describedby={isUnavailable ? describedBy : undefined}
      className={cn(
        'w-full h-[120px] rounded-card flex items-center justify-center gap-3 text-lg font-semibold tracking-tight transition-all duration-200',
        'active:scale-[0.99] disabled:pointer-events-none',
        isUnavailable
          ? 'border border-border-strong bg-bg-surface text-text-muted'
          : 'bg-brand-dark text-bg-elevated',
      )}
    >
      {isBusy ? (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-brand-accent" aria-hidden />
          <span>{status === 'gps' ? 'Sprawdzam GPS…' : 'Zapisuję…'}</span>
        </>
      ) : mode === 'end' ? (
        <>
          <Square className={iconClass} aria-hidden />
          <span>Koniec pracy</span>
        </>
      ) : mode === 'start' ? (
        <>
          <Play className={iconClass} aria-hidden />
          <span>Zaczynam pracę</span>
        </>
      ) : (
        <>
          <Clock4 className={iconClass} aria-hidden />
          <span>Start / koniec pracy</span>
        </>
      )}
    </button>
  )
}
